import { useLayoutEffect } from "@react-aria/utils"
import PropTypes from "prop-types"
import { forwardRef, useCallback, useRef } from "react"

const Expand = forwardRef(({ children, show, ...props }, ref) => {
  const internalRef = useRef(null)
  const isMounted = useRef(false)

  const mergeRef = useCallback(
    (node) => {
      internalRef.current = node
      if (typeof ref == "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  useLayoutEffect(() => {
    const el = internalRef.current
    if (!el) return

    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    let cancelled = false

    const onTransitionEnd = (e) => {
      if (cancelled) return
      if (e.target !== el) return

      if (show) {
        el.style.height = "auto"
        el.style.overflow = "visible"
      } else {
        el.style.display = "none"
      }
      el.style.transition = ""
    }

    if (show) {
      el.style.display = "block"
      el.style.overflow = "hidden"
      el.style.transition = ""

      el.style.height = el.style.height || "0px"

      void el.offsetHeight

      el.style.transition = "height 300ms ease-out"
      el.style.height = `${el.scrollHeight}px`
    } else {
      el.style.display = "block"
      el.style.overflow = "hidden"
      el.style.transition = ""
      el.style.height = `${el.scrollHeight}px`

      void el.offsetHeight

      el.style.transition = "height 300ms ease-out"
      el.style.height = "0px"
    }

    el.addEventListener("transitionend", onTransitionEnd, { once: true })

    return () => {
      cancelled = true
      el.removeEventListener("transitionend", onTransitionEnd)
    }
  }, [show])

  return (
    <div
      ref={mergeRef}
      style={show ? undefined : { display: "none" }}
      {...props}
    >
      {children}
    </div>
  )
})

Expand.displayName = "Expand"

Expand.propTypes = {
  children: PropTypes.node,
  show: PropTypes.bool,
}

export default Expand
